import type { StreamEvent } from '../../shared/events';
import { buildEulerWsUrl, normalizeEulerEnvelope, type EulerEnvelope } from '../../shared/euler';

export interface EulerHandlers {
  onOpen: () => void;
  onEvents: (events: StreamEvent[]) => void;
  onClose: (code: number, reason: string) => void;
}

/** Direct browser connection to Euler Stream's cloud WebSocket for one TikTok LIVE. */
export class EulerClient {
  private ws: WebSocket | null = null;
  private closedByUs = false;

  constructor(private handlers: EulerHandlers) {}

  connect(username: string, jwt: string): void {
    this.connectUrl(buildEulerWsUrl(username, jwt));
  }

  /** Any WebSocket that speaks Euler's envelope format, such as BacaChat's logged-in relay. */
  connectUrl(url: string): void {
    this.close();
    this.closedByUs = false;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => this.handlers.onOpen();
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      let env: EulerEnvelope;
      try {
        env = JSON.parse(ev.data) as EulerEnvelope;
      } catch {
        return;
      }
      const events = normalizeEulerEnvelope(env);
      if (events.length) this.handlers.onEvents(events);
    };
    ws.onclose = (ev) => {
      if (this.ws === ws) this.ws = null;
      if (!this.closedByUs) this.handlers.onClose(ev.code, ev.reason);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  get open(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.closedByUs = true;
    try {
      this.ws?.close(1000, 'client closed');
    } catch {
      /* ignore */
    }
    this.ws = null;
  }
}
